import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command, CommandEmpty, CommandGroup,
  CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ncmService } from "@/features/fiscal/services/fiscalService";

interface Props {
  value?:       number;          // codigoInterno
  valueCodigo?: string;          // alternativa: código NCM (string) — usado quando o cadastro guarda o código, não o id interno
  onChange:     (codigoInterno: number, codigo: string, descricao: string) => void;
  placeholder?: string;
  disabled?:    boolean;
}

export function NcmSearchCombobox({
  value,
  valueCodigo,
  onChange,
  placeholder = "Buscar NCM por código ou descrição...",
  disabled,
}: Props) {
  const [open, setOpen]     = useState(false);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["ncm-search", search],
    queryFn:  () => ncmService.listar(search, true, 1, 50),
    enabled:  open && search.trim().length >= 2,
    staleTime: 15_000,
  });

  const results = data?.itens ?? [];

  // Busca o NCM selecionado para exibir no botão (por id interno OU por código string)
  const porCodigo = valueCodigo != null && valueCodigo.trim().length > 0;
  const { data: pageSelecionado } = useQuery({
    queryKey: ["ncm-search-selected", value, valueCodigo],
    queryFn:  () => porCodigo
      ? ncmService.listar(valueCodigo, undefined, 1, 50)
      : ncmService.listar(undefined, undefined, 1, 300),
    enabled:  porCodigo || !!value,
    staleTime: 60_000,
    select:   (d) => porCodigo
      ? d.itens.find(n => n.codigo === valueCodigo)
      : d.itens.find(n => n.codigoInterno === value),
  });

  const label = pageSelecionado
    ? `${pageSelecionado.codigo} — ${pageSelecionado.descricao}`
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          disabled={disabled}
          type="button"
        >
          <span className="truncate text-left">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[520px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Digite código ou descrição do NCM..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoading && (
              <CommandEmpty>
                {search.trim().length < 2
                  ? "Digite ao menos 2 caracteres para buscar..."
                  : "Nenhum NCM encontrado."}
              </CommandEmpty>
            )}
            <CommandGroup>
              {results.map(n => (
                <CommandItem
                  key={n.codigoInterno}
                  value={String(n.codigoInterno)}
                  onSelect={() => {
                    onChange(n.codigoInterno, n.codigo, n.descricao);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", (porCodigo ? n.codigo === valueCodigo : value === n.codigoInterno) ? "opacity-100" : "opacity-0")} />
                  <div className="flex flex-col min-w-0">
                    <span className="font-mono text-sm">{n.codigo}</span>
                    <span className="text-xs text-muted-foreground truncate">{n.descricao}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
